import React, { useCallback, useId, useMemo } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { getOverrides, useClasses } from '../../../utils/overrides';
import Icon from '../../general/Icon';
import PromoGradientDefs from '../../../utils/styles/PromoGradientDefs';

import { createUseStyles, useTheme } from '../../../utils/styles';
import styles from './styles';
const useStyles = createUseStyles(styles, 'Checkbox');

const defaultColors = [
    'grey',
    'semanticNegative',
    'actionMajor',
    'actionMinor',
    'semanticPositive',
    'semanticInfo',
    'semanticFocus',
    'purple',
];

function Checkbox({
    classes: classesProp,
    overrides: overridesProp,
    className: classNameProp,
    checked,
    indeterminate,
    isDisabled,
    color: colorProp = 'grey',
    isMonotone,
    isEnriched = false,
    onChange = () => {},
    size = 'medium',
}) {
    const theme = useTheme();
    const classes = useClasses(useStyles, classesProp);
    const gradientId = `promo-gradient-${useId().replace(/:/g, '')}`;
    // Overrides
    const override = getOverrides(overridesProp, Checkbox.overrides);

    // Classes
    const rootClassName = classnames(
        classes.root,
        {
            [classes.disabled]: isDisabled,
        },
        classNameProp,
    );

    const { colorEnabled, colorDisabled } = useMemo(() => {
        let disabled = theme.colors.grey[200];
        let enabled = theme.colors.grey[500];

        switch (colorProp) {
            case 'grey':
                disabled = theme.colors.grey[200];
                enabled = theme.colors.grey[500];
                break;
            case 'actionMajor':
                disabled = theme.colors.actionMajor[150];
                enabled = theme.colors.actionMajor[500];
                break;
            case 'actionMinor':
                disabled = theme.colors.actionMinor[150];
                enabled = theme.colors.actionMinor[400];
                break;
            case 'semanticPositive':
                disabled = theme.colors.semantic.positiveCustom200;
                enabled = theme.colors.semantic.positive500;
                break;
            case 'semanticNegative':
                disabled = theme.colors.semantic.negativeCustom200;
                enabled = theme.colors.semantic.negative500;
                break;
            case 'semanticInfo':
                disabled = theme.colors.semantic.infoCustom100;
                enabled = theme.colors.semantic.info500;
                break;
            case 'semanticFocus':
                disabled = theme.colors.semantic.focusCustom200;
                enabled = theme.colors.semantic.focus500;
                break;
            case 'purple':
                disabled = theme.colors.temp.purple200;
                enabled = theme.colors.temp.purple500;
                break;
            default:
                break;
        }

        return { colorEnabled: enabled, colorDisabled: disabled };
    }, [
        colorProp,
        theme.colors.actionMajor,
        theme.colors.actionMinor,
        theme.colors.grey,
        theme.colors.semantic.focus500,
        theme.colors.semantic.focusCustom200,
        theme.colors.semantic.info500,
        theme.colors.semantic.infoCustom100,
        theme.colors.semantic.negative500,
        theme.colors.semantic.negativeCustom200,
        theme.colors.semantic.positive500,
        theme.colors.semantic.positiveCustom200,
        theme.colors.temp.purple200,
        theme.colors.temp.purple500,
    ]);

    const getColor = useCallback(
        (checkState) => {
            if (isMonotone) {
                if (colorProp === 'grey') {
                    return isDisabled ? theme.colors.grey[200] : theme.colors.grey[500];
                }
                return isDisabled
                    ? colorDisabled || theme.colors.grey[200]
                    : colorEnabled || theme.colors.grey[500];
            } else {
                return isDisabled
                    ? colorDisabled || theme.colors.grey[200]
                    : colorEnabled || theme.colors.grey[500];
            }
        },
        [isMonotone, colorProp, isDisabled, colorDisabled, theme.colors.grey, colorEnabled],
    );

    const rootProps = {
        className: rootClassName,
        onClick: !isDisabled ? onChange : undefined,
    };

    let checkState = 'unchecked';
    if (checked && indeterminate) checkState = 'indeterminate';
    else if (checked) checkState = 'checked';

    const iconColor = isEnriched ? `url(#${gradientId})` : getColor(checkState);

    return (
        <div {...rootProps} {...override.root}>
            {isEnriched && (
                <svg
                    aria-hidden="true"
                    focusable="false"
                    width="0"
                    height="0"
                    style={{ position: 'absolute', overflow: 'hidden' }}
                >
                    <PromoGradientDefs id={gradientId} />
                </svg>
            )}
            {checkState === 'checked' && (
                <Icon name="checkBox" size={size} color={iconColor} {...override.svg} />
            )}
            {checkState === 'unchecked' && (
                <Icon name="checkBoxOutlineBlank" size={size} color={iconColor} {...override.svg} />
            )}
            {checkState === 'indeterminate' && (
                <Icon
                    name="indeterminateCheckBox"
                    size={size}
                    color={iconColor}
                    {...override.svg}
                />
            )}
            <input
                className={classes.input}
                onChange={!isDisabled ? onChange : undefined}
                type="checkbox"
                checked={checked ? 'checked' : ''}
                value={checkState}
                disabled={isDisabled ? 'disabled' : ''}
                {...override.input}
            />
        </div>
    );
}

Checkbox.overrides = ['root', 'input', 'svg'];

Checkbox.propTypes = {
    /** Custom className for the root element */
    className: PropTypes.string,
    /** Object used to override component styles at different component tree levels */
    overrides: PropTypes.object,
    /** Whether the checkbox is checked */
    checked: PropTypes.bool,
    /** Whether the checkbox is in an indeterminate state */
    indeterminate: PropTypes.bool,
    /** Function called when the checkbox state changes */
    onChange: PropTypes.func,
    /** Whether the checkbox is disabled */
    isDisabled: PropTypes.bool,
    /** When true, fills the checkbox SVG with the promotional gradient to indicate AI-enriched content */
    isEnriched: PropTypes.bool,
    /** The color theme of the checkbox */
    color: PropTypes.oneOf(defaultColors),
    /** The size of the checkbox */
    size: PropTypes.oneOf(['small', 'medium', 'large', 'big', 'huge']),
    /** Whether to use monotone coloring (same color for outline and fill) */
    isMonotone: PropTypes.bool,
};

export default React.memo(Checkbox);
